import { Component } from 'react'
import { reportClientError } from '../lib/reportError'
import { isStaleChunkError, isTransientNetworkError } from '../lib/loadErrorClass'

// PaneErrorBoundary — granular error boundary for a single module / tab / widget.
//
// Unlike the app-wide boundary (which blanks the whole screen on any crash),
// this contains a failure to ONE pane: the rest of the app keeps working and
// the broken pane shows a small "couldn't load — retry" card. Retry re-mounts
// the subtree in place (no page reload). It also auto-resets when `resetKeys`
// change, so navigating to a different email / route / tab clears a stuck error
// without the user doing anything.
//
// Usage:
//   <PaneErrorBoundary label="Intelligence" resetKeys={[email.id]}>
//     <IntelligenceTab .../>
//   </PaneErrorBoundary>
//
// Props:
//   label      – friendly name shown in the card ("Intelligence", "this widget")
//   resetKeys  – array; when any value changes, the boundary clears its error
//   compact    – smaller card for tight spaces (widgets)
//   onRetry    – optional callback fired on retry (e.g. to refetch)
class PaneErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, message: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown error' }
  }

  componentDidCatch(error, info) {
    // Stale-deploy errors bubble to the app boundary which reloads — don't
    // swallow those here. Network blips stay contained in the pane: bubbling
    // them used to trigger the app-wide reload loop for a transient failure.
    const msg = error?.message || ''
    if (isStaleChunkError(msg)) {
      throw error
    }
    console.error(`[PaneErrorBoundary:${this.props.label || 'pane'}]`, msg, error)
    if (isTransientNetworkError(msg)) return // transient — retry card is enough, don't report
    reportClientError({
      error_message:   `[pane:${this.props.label || 'unknown'}] ${msg}`,
      error_stack:     error?.stack,
      component_stack: info?.componentStack,
      error_type:      'react_pane_boundary',
      page_url:        typeof window !== 'undefined' ? window.location.href : null,
    })
  }

  componentDidUpdate(prevProps) {
    // Auto-reset when any resetKey changes (e.g. user navigated to a new item).
    if (!this.state.hasError) return
    const a = prevProps.resetKeys || []
    const b = this.props.resetKeys || []
    if (a.length !== b.length || a.some((v, i) => v !== b[i])) {
      this.setState({ hasError: false, message: null })
    }
  }

  retry = () => {
    this.setState({ hasError: false, message: null })
    try { this.props.onRetry?.() } catch { /* non-fatal */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const label = this.props.label || 'this section'
    if (this.props.compact) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
          <span className="text-lg">⚠️</span>
          <p className="text-xs text-warm-500">Couldn’t load {label}.</p>
          <button onClick={this.retry} className="text-xs text-navy-600 hover:text-navy-800 underline">Retry</button>
        </div>
      )
    }

    return (
      <div className="flex flex-col items-center justify-center gap-3 p-8 min-h-[200px] text-center">
        <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
          <span className="text-xl">⚠️</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-warm-800">Couldn’t load {label}</p>
          <p className="text-xs text-warm-500 mt-0.5">The rest of the app is still working. Janet has been notified.</p>
        </div>
        {this.state.message && (
          <p className="text-[11px] font-mono text-warm-400 bg-warm-50 rounded-lg p-2 max-w-sm break-all">
            {this.state.message}
          </p>
        )}
        <button onClick={this.retry} className="btn-secondary text-xs">Retry</button>
      </div>
    )
  }
}

export default PaneErrorBoundary
